import {
  Anchor,
  Box,
  Button,
  Card,
  Container,
  Divider,
  Group,
  NumberInput,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { createFileRoute, Link } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuArrowLeft, LuBuilding2, LuMail, LuMapPin, LuSettings } from 'react-icons/lu'
import { useMarinaSettings, useUpdateMarinaSettings } from '@/frontend/hooks/useSettings'

export const Route = createFileRoute('/dashboard/settings/marina-info')({
  component: MarinaInfoPage,
})

const TIMEZONES = [
  { value: 'Asia/Makassar', label: 'Asia/Makassar (WITA — Bali, Lombok, Sulawesi)' },
  { value: 'Asia/Jakarta', label: 'Asia/Jakarta (WIB — Jawa, Sumatera)' },
  { value: 'Asia/Jayapura', label: 'Asia/Jayapura (WIT — Papua, Maluku)' },
  { value: 'UTC', label: 'UTC' },
]
const CURRENCIES = [
  { value: 'IDR', label: 'IDR (Rp) — Rupiah Indonesia' },
  { value: 'USD', label: 'USD ($) — US Dollar' },
  { value: 'EUR', label: 'EUR (€) — Euro' },
  { value: 'SGD', label: 'SGD (S$) — Singapore Dollar' },
]

function MarinaInfoPage() {
  const { t } = useTranslation()
  const { data, isLoading } = useMarinaSettings()
  const update = useUpdateMarinaSettings()
  const s = data?.settings

  const [form, setForm] = useState({
    name: '',
    address: '',
    city: '',
    country: 'Indonesia',
    timezone: 'Asia/Makassar',
    currency: 'IDR',
    defaultTaxRate: 11,
    supportEmail: '',
    supportPhone: '',
    logoUrl: '',
  })

  useEffect(() => {
    if (s) {
      setForm({
        name: s.name,
        address: s.address ?? '',
        city: s.city ?? '',
        country: s.country,
        timezone: s.timezone,
        currency: s.currency,
        defaultTaxRate: Number(s.defaultTaxRate),
        supportEmail: s.supportEmail ?? '',
        supportPhone: s.supportPhone ?? '',
        logoUrl: s.logoUrl ?? '',
      })
    }
  }, [s])

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }))

  const submit = async () => {
    try {
      await update.mutateAsync({
        name: form.name,
        address: form.address || null,
        city: form.city || null,
        country: form.country,
        timezone: form.timezone,
        currency: form.currency,
        defaultTaxRate: form.defaultTaxRate,
        supportEmail: form.supportEmail || null,
        supportPhone: form.supportPhone || null,
        logoUrl: form.logoUrl || null,
      })
      notifications.show({
        title: t('page.settings.marina.notify_saved'),
        message: t('page.settings.marina.notify_saved_msg'),
        color: 'teal',
      })
    } catch (e: unknown) {
      notifications.show({
        title: t('common.error'),
        message: e instanceof Error ? e.message : t('common.try_again'),
        color: 'red',
      })
    }
  }

  const logoIsUrl = form.logoUrl.startsWith('http')
  const docPreviewName = form.name || 'Nama Marina'
  const docPreviewLocation = [form.city, form.country].filter(Boolean).join(', ')

  return (
    <Container size="md" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Group gap="sm" align="center">
          <Anchor component={Link} to="/dashboard/settings" c="dimmed" style={{ display: 'flex' }}>
            <LuArrowLeft size={18} />
          </Anchor>
          <ThemeIcon variant="light" color="laut" size="lg" radius="md">
            <LuSettings size={18} />
          </ThemeIcon>
          <div>
            <Title order={2} ff="var(--font-display)" fw={600}>
              {t('page.settings.marina.title')}
            </Title>
            <Text size="xs" c="dimmed">
              {t('page.settings.marina.subtitle')}
            </Text>
          </div>
        </Group>

        {isLoading ? (
          <Stack gap="md">
            <Skeleton height={140} radius="md" />
            <Skeleton height={200} radius="md" />
            <Skeleton height={100} radius="md" />
          </Stack>
        ) : (
          <>
            {/* Seksi 1 — Identitas */}
            <Card padding="lg" radius="md">
              <Group gap="xs" mb="md">
                <ThemeIcon variant="light" color="laut" size="sm" radius="sm">
                  <LuBuilding2 size={13} />
                </ThemeIcon>
                <Text fw={600} size="sm">
                  {t('page.settings.marina.section_identity')}
                </Text>
              </Group>

              <Stack gap="md">
                <TextInput
                  label={t('page.settings.marina.field_name')}
                  required
                  placeholder={t('page.settings.marina.field_name_placeholder')}
                  description={t('page.settings.marina.field_name_desc')}
                  value={form.name}
                  onChange={(e) => set({ name: e.currentTarget.value })}
                />

                <div>
                  <TextInput
                    label={t('page.settings.marina.field_logo')}
                    placeholder={t('page.settings.marina.field_logo_placeholder')}
                    description={t('page.settings.marina.field_logo_desc')}
                    value={form.logoUrl}
                    onChange={(e) => set({ logoUrl: e.currentTarget.value })}
                  />
                  {logoIsUrl && (
                    <Box
                      mt="xs"
                      p="xs"
                      style={{
                        borderRadius: 8,
                        background: 'var(--app-card-bg)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <img
                        src={form.logoUrl}
                        alt="Logo preview"
                        style={{ height: 36, width: 'auto', maxWidth: 160, display: 'block', objectFit: 'contain' }}
                        onError={(e) => {
                          ;(e.target as HTMLImageElement).style.display = 'none'
                        }}
                      />
                    </Box>
                  )}
                </div>

                {/* Pratinjau dokumen */}
                <Box
                  px="md"
                  py="sm"
                  style={{
                    borderRadius: 8,
                    background: 'light-dark(var(--mantine-color-laut-0), rgba(14,124,123,0.1))',
                    borderLeft: '3px solid var(--mantine-color-laut-4)',
                  }}
                >
                  <Text size="xs" c="dimmed" mb={4}>
                    {t('page.settings.marina.preview_label')}
                  </Text>
                  <Text size="sm" fw={700}>
                    {docPreviewName}
                  </Text>
                  {docPreviewLocation && (
                    <Text size="xs" c="dimmed">
                      {docPreviewLocation}
                    </Text>
                  )}
                </Box>
              </Stack>
            </Card>

            {/* Seksi 2 — Lokasi & Regional */}
            <Card padding="lg" radius="md">
              <Group gap="xs" mb="md">
                <ThemeIcon variant="light" color="laut" size="sm" radius="sm">
                  <LuMapPin size={13} />
                </ThemeIcon>
                <Text fw={600} size="sm">
                  {t('page.settings.marina.section_location')}
                </Text>
              </Group>

              <Stack gap="md">
                <Textarea
                  label={t('page.settings.marina.field_address')}
                  rows={2}
                  placeholder={t('page.settings.marina.field_address_placeholder')}
                  value={form.address}
                  onChange={(e) => set({ address: e.currentTarget.value })}
                />

                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  <TextInput
                    label={t('page.settings.marina.field_city')}
                    placeholder={t('page.settings.marina.field_city_placeholder')}
                    value={form.city}
                    onChange={(e) => set({ city: e.currentTarget.value })}
                  />
                  <TextInput
                    label={t('page.settings.marina.field_country')}
                    placeholder="Indonesia"
                    value={form.country}
                    onChange={(e) => set({ country: e.currentTarget.value })}
                  />
                </SimpleGrid>

                <Divider />

                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  <Select
                    label={t('page.settings.marina.field_timezone')}
                    data={TIMEZONES}
                    value={form.timezone}
                    onChange={(v) => set({ timezone: v ?? 'Asia/Makassar' })}
                  />
                  <Select
                    label={t('page.settings.marina.field_currency')}
                    data={CURRENCIES}
                    value={form.currency}
                    onChange={(v) => set({ currency: v ?? 'IDR' })}
                  />
                </SimpleGrid>

                <NumberInput
                  label={t('page.settings.marina.field_tax')}
                  description={t('page.settings.marina.field_tax_desc')}
                  value={form.defaultTaxRate}
                  onChange={(v) => set({ defaultTaxRate: typeof v === 'number' ? v : 0 })}
                  min={0}
                  max={100}
                  suffix="%"
                  maw={200}
                />
              </Stack>
            </Card>

            {/* Seksi 3 — Kontak Dukungan */}
            <Card padding="lg" radius="md">
              <Group gap="xs" mb="md">
                <ThemeIcon variant="light" color="laut" size="sm" radius="sm">
                  <LuMail size={13} />
                </ThemeIcon>
                <Text fw={600} size="sm">
                  {t('page.settings.marina.section_contact')}
                </Text>
              </Group>

              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <TextInput
                  label={t('page.settings.marina.field_email')}
                  type="email"
                  placeholder={t('page.settings.marina.field_email_placeholder')}
                  description={t('page.settings.marina.field_email_desc')}
                  value={form.supportEmail}
                  onChange={(e) => set({ supportEmail: e.currentTarget.value })}
                />
                <TextInput
                  label={t('page.settings.marina.field_phone')}
                  placeholder={t('page.settings.marina.field_phone_placeholder')}
                  description={t('page.settings.marina.field_phone_desc')}
                  value={form.supportPhone}
                  onChange={(e) => set({ supportPhone: e.currentTarget.value })}
                />
              </SimpleGrid>
            </Card>

            {/* Footer simpan */}
            <Group justify="space-between" align="center">
              {s?.updatedAt ? (
                <Text size="xs" c="dimmed">
                  {t('page.settings.marina.last_saved', { datetime: dayjs(s.updatedAt).format('DD MMM YYYY, HH:mm') })}
                </Text>
              ) : (
                <span />
              )}
              <Button color="laut" onClick={submit} loading={update.isPending} size="sm">
                {t('page.settings.marina.btn_save')}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Container>
  )
}
